import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { apiService } from '@/services/api'
import { useSettingsStore } from '@/store/settingsStore'

// Role definitions
export type UserRole = 'admin' | 'manager' | 'cashier' | 'staff'

export const ROLE_HIERARCHY: Record<UserRole, number> = {
  admin: 4,
  manager: 3,
  cashier: 2,
  staff: 1
}

// Role permissions mapping
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  admin: ['*'], // Full access
  manager: [
    'dashboard', 'pos', 'inventory', 'inventory:edit', 
    'customers', 'customers:edit', 'reports', 'ai-insights', 
    'employees', 'employees:view'
  ],
  cashier: [
    'dashboard', 'pos', 'customers', 'customers:view'
  ],
  staff: [
    'dashboard', 'pos', 'inventory:view'
  ]
}

// Role display names and colors
export const ROLE_CONFIG: Record<UserRole, { label: string; color: string; bgColor: string }> = {
  admin: { label: 'Administrator', color: 'text-red-400', bgColor: 'bg-red-500/20' },
  manager: { label: 'Manager', color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
  cashier: { label: 'Cashier', color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  staff: { label: 'Staff', color: 'text-green-400', bgColor: 'bg-green-500/20' }
}

interface User {
  id: string
  email: string
  role: UserRole
  name: string
}

interface LoginResponse {
  success: boolean
  message: string
  token?: string
  user?: User
}

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  loading: boolean
  /** True when user was logged out due to session timeout (inactivity). */
  sessionExpired: boolean
  /** Call to reset session-expired message after user has seen it. */
  clearSessionExpired: () => void
  /** Call to refresh last activity timestamp (resets idle timer). */
  refreshSessionActivity: () => void
  login: (email: string, password: string) => Promise<LoginResponse>
  loginWithPin: (email: string, pin: string) => Promise<LoginResponse>
  logout: () => void
  // Role helpers
  hasRole: (role: UserRole) => boolean
  hasMinRole: (minRole: UserRole) => boolean
  hasPermission: (permission: string) => boolean
  isAdmin: boolean
  isManager: boolean
  isCashier: boolean
  isStaff: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

const IDLE_CHECK_INTERVAL_MS = 60 * 1000 // 1 minute
const ACTIVITY_THROTTLE_MS = 30 * 1000   // refresh at most every 30s on activity

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [sessionExpired, setSessionExpired] = useState(false)
  const lastActivityRef = useRef<number>(Date.now())
  const lastRefreshRef = useRef<number>(0)

  const refreshSessionActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
  }, [])

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false)
  }, [])

  useEffect(() => {
    // Check authentication status on app load
    try {
      const authStatus = localStorage.getItem('isAuthenticated')
      const userData = localStorage.getItem('user')
      const token = localStorage.getItem('token')
      
      if (authStatus === 'true' && userData && token) {
        try {
          const parsedUser = JSON.parse(userData)
          // Ensure role is valid, default to 'staff'
          if (!parsedUser.role || !['admin', 'manager', 'cashier', 'staff'].includes(parsedUser.role)) {
            parsedUser.role = 'staff'
          }
          setIsAuthenticated(true)
          setUser(parsedUser)
          // Set token in API service so it's included in requests
          apiService.setAuthToken(token)
        } catch (parseError) {
          console.error('Error parsing user data:', parseError)
          // Clear invalid data
          localStorage.removeItem('isAuthenticated')
          localStorage.removeItem('user')
          localStorage.removeItem('token')
          setIsAuthenticated(false)
          setUser(null)
          apiService.clearAuthToken()
        }
      } else {
        setIsAuthenticated(false)
        setUser(null)
        apiService.clearAuthToken()
      }
    } catch (error) {
      console.error('Error checking auth status:', error)
      setIsAuthenticated(false)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Session timeout: idle logout based on settings
  useEffect(() => {
    if (!isAuthenticated) return
    const sessionTimeoutMinutes = useSettingsStore.getState().settings.sessionTimeoutMinutes
    if (sessionTimeoutMinutes <= 0) return

    const intervalId = setInterval(() => {
      const timeoutMs = sessionTimeoutMinutes * 60 * 1000
      if (Date.now() - lastActivityRef.current >= timeoutMs) {
        setSessionExpired(true)
        localStorage.removeItem('isAuthenticated')
        localStorage.removeItem('user')
        localStorage.removeItem('token')
        setIsAuthenticated(false)
        setUser(null)
        apiService.clearAuthToken()
      }
    }, IDLE_CHECK_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [isAuthenticated])

  // Global activity listeners to refresh idle timer (throttled)
  useEffect(() => {
    if (!isAuthenticated) return
    const handleActivity = () => {
      const now = Date.now()
      if (now - lastRefreshRef.current < ACTIVITY_THROTTLE_MS) return
      lastRefreshRef.current = now
      lastActivityRef.current = now
    }
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach(ev => window.addEventListener(ev, handleActivity))
    return () => events.forEach(ev => window.removeEventListener(ev, handleActivity))
  }, [isAuthenticated])

  // Role helper functions
  const hasRole = useCallback((role: UserRole): boolean => {
    return user?.role === role
  }, [user])

  const hasMinRole = useCallback((minRole: UserRole): boolean => {
    if (!user?.role) return false
    const userLevel = ROLE_HIERARCHY[user.role] || 0
    const requiredLevel = ROLE_HIERARCHY[minRole] || 0
    return userLevel >= requiredLevel
  }, [user])

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user?.role) return false
    const permissions = ROLE_PERMISSIONS[user.role] || []
    // Admin has all permissions
    if (permissions.includes('*')) return true
    // Check exact match or wildcard match (e.g., 'inventory' matches 'inventory:edit')
    return permissions.some(p => 
      p === permission || 
      permission.startsWith(`${p}:`) ||
      p.startsWith(`${permission}:`)
    )
  }, [user])

  // Convenience role checks
  const isAdmin = useMemo(() => user?.role === 'admin', [user])
  const isManager = useMemo(() => hasMinRole('manager'), [hasMinRole])
  const isCashier = useMemo(() => hasMinRole('cashier'), [hasMinRole])
  const isStaff = useMemo(() => hasMinRole('staff'), [hasMinRole])

  const login = async (email: string, password: string): Promise<LoginResponse> => {
    setLoading(true)
    try {
      // Use the API service URL from environment or default to production
      const defaultUrl = import.meta.env.DEV ? 'http://localhost:5000' : 'https://esales-api-142602456566.us-central1.run.app'
      let apiUrl = import.meta.env.VITE_API_URL || defaultUrl
      // Ensure /api prefix
      if (!apiUrl.endsWith('/api')) {
        apiUrl = `${apiUrl}/api`
      }
      const loginUrl = `${apiUrl}/auth/login`
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Login failed' }))
        return { success: false, message: errorData.message || `HTTP ${response.status}: Login failed` }
      }

      const data = await response.json()

      if (data.success && data.user) {
        // Ensure user object has required fields and valid role
        const userRole = ['admin', 'manager', 'cashier', 'staff'].includes(data.user.role) 
          ? data.user.role as UserRole 
          : 'staff'
        
        const userData: User = {
          id: data.user.id || data.user._id || '',
          email: data.user.email || '',
          role: userRole,
          name: data.user.name || ''
        }
        
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('user', JSON.stringify(userData))
        localStorage.setItem('token', data.token || '')
        setIsAuthenticated(true)
        setUser(userData)
        // Set token in API service so it's included in all requests
        if (data.token) {
          apiService.setAuthToken(data.token)
        }
        return { success: true, message: data.message || 'Login successful', user: userData, token: data.token }
      } else {
        return { success: false, message: data.message || 'Login failed' }
      }
    } catch (error: any) {
      console.error('Login error:', error)
        return { success: false, message: error.message || 'Network error. Please try again.' }
    } finally {
      setLoading(false)
    }
  }

  const loginWithPin = async (email: string, pin: string): Promise<LoginResponse> => {
    setLoading(true)
    try {
      let apiUrl = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:5000' : 'https://esales-api-142602456566.us-central1.run.app')
      if (!apiUrl.endsWith('/api')) apiUrl = `${apiUrl}/api`
      const response = await fetch(`${apiUrl}/auth/login-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), pin }),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Login failed' }))
        return { success: false, message: errorData.message || `Login failed` }
      }
      const data = await response.json()
      if (data.success && data.user) {
        const userRole = ['admin', 'manager', 'cashier', 'staff'].includes(data.user.role) ? data.user.role as UserRole : 'staff'
        const userData: User = {
          id: data.user.id || data.user._id || '',
          email: data.user.email || '',
          role: userRole,
          name: data.user.name || ''
        }
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('user', JSON.stringify(userData))
        localStorage.setItem('token', data.token || '')
        setIsAuthenticated(true)
        setUser(userData)
        if (data.token) apiService.setAuthToken(data.token)
        return { success: true, message: 'Login successful', user: userData, token: data.token }
      }
      return { success: false, message: data.message || 'Login failed' }
    } catch (error: any) {
      return { success: false, message: error.message || 'Network error.' }
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    setSessionExpired(false)
    localStorage.removeItem('isAuthenticated')
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    setIsAuthenticated(false)
    setUser(null)
    apiService.clearAuthToken()
  }

  const contextValue = useMemo(() => ({
    isAuthenticated,
    user,
    loading,
    sessionExpired,
    clearSessionExpired,
    refreshSessionActivity,
    login,
    loginWithPin,
    logout,
    // Role helpers
    hasRole,
    hasMinRole,
    hasPermission,
    isAdmin,
    isManager,
    isCashier,
    isStaff
  }), [isAuthenticated, user, loading, sessionExpired, clearSessionExpired, refreshSessionActivity, login, loginWithPin, hasRole, hasMinRole, hasPermission, isAdmin, isManager, isCashier, isStaff])

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}
